import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { GameCategory, GameRedirectionSource } from '../../../../enums';
import { VirtualGame } from '../../../../models/virtual-games';
import { useStore, useTranslation } from '../../../../hooks';
import { GameCategoryNames, T } from '../../../../constants';
import { integration } from '../../../../services';
import { GameFrame } from '../../game-frame';

import MobileGameOpenerIcon from '../../../../assets/media/virtual-launcher/games-opener-mobile.png'
import SideBarOpenerIcon from '../../../../assets/media/virtual-launcher/sidebar-opener.png'
import GameItemIcon from '../../../../assets/media/virtual-launcher/game-logo.png'
import CloseIcon from '../../../../assets/media/virtual-launcher/close.png'

import './style.scss';

interface VirtualGameLaunchProps {
  frameNonce?: number;
  gameFrameSource: string;
  refCallback: React.Ref<HTMLIFrameElement>;
}

export const VirtualGameLaunch: React.FC<VirtualGameLaunchProps> = observer(({ gameFrameSource, refCallback, frameNonce }) => {
  const { gameStore } = useStore();
  const { launchData: { data: launchData }, virtualGames: { data: virtualGames } } = gameStore;
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<any | null>(launchData?.gameId);
  const [selectedCategory, setSelectedCategory] = useState<number>(launchData?.category ?? 0);
  const t = useTranslation();

  useEffect(() => {
    if (launchData) {
      const { currency, partnerId, gameCategories } = launchData;
      gameStore.getVirtualGames({ currencyCode: currency, partnerId, gameCategories });
    }
  }, []);

  const changeGame = useCallback((gameId: number) => {
    integration.current.changeGame({ gameId, source: GameRedirectionSource.Game });
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const groupedCategories = useMemo(() => {
    if (!virtualGames) return;
    return virtualGames.reduce((acc: Record<number, any[]>, game) => {
      const { category } = game;

      if (!acc[category]) {
        acc[category] = [];
      } 
      acc[category].push(game);

      return acc;
    }, {});
  }, [virtualGames]);  

  const onCategoryChange = useCallback((category: number) => {
    setSelectedCategory(category);
    const first = groupedCategories?.[category]?.[0];
    setSelectedGame(first);
    changeGame(first.gameId);
  }, [groupedCategories, changeGame]);

  const onGameSelect = useCallback((game: VirtualGame) => {
    setSelectedGame(game);
    setSidebarOpen(false);
    changeGame(game.gameId);
  }, [changeGame]);

  const isNumberGamesSelected = useMemo(() => {
    return +selectedCategory === GameCategory.NumberGames;
  }, [selectedCategory]);

  return (
    gameFrameSource ? (
      <div className="vg-wrapper">
        <header className="header">
          {launchData?.gameCategories.map((category, index) => {
            const isNewBadge = category === GameCategory.Instant || category === GameCategory.NumberGames;

            return (
              <button
                key={category}
                className={`virtual-btn ${isNewBadge ? 'new-badge' : ''} ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => onCategoryChange(category)}
              >
                {GameCategoryNames[category]}
              </button>
            );
          })}
        </header>


        <main className="main">
          <div className={`game-subheader ${!isNumberGamesSelected ? 'hide' : ''}`}>
            <div className="game-subheader-left">
              <div className="spin-menu-icon" onClick={toggleSidebar}>
                <img src={MobileGameOpenerIcon} alt="games-opener" />
              </div>
              <div className="spin-menu-text ellipsis">{selectedGame?.gameName}</div>
            </div>

            <div className="game-subheader-right">
              <span className="spin-menu-deposit ellipsis">
                {t(T.MIN)}: <span>{selectedGame?.minBetAmount ?? 0}</span>
              </span>
              <span className="spin-menu-deposit ellipsis">
                {t(T.MAX)}: <span>{selectedGame?.maxBetAmount ?? 0}</span>
              </span>
            </div>
          </div>


          <div className={`games-section 
            ${!isNumberGamesSelected ? 'hide' : ''}
            ${isSidebarOpen ? 'closed' : ''}
          `}>
            <div className="vg-nav-holder">
              <div className="nav-holder-header">
                <div className="nav-holder-header-title-wrap">
                  <span className="nav-holder-header-title ellipsis">{t(T.ALL_GAMES)}</span>
                </div>
                <div className="header-close-icon" onClick={() => setSidebarOpen(false)}>
                  <img src={CloseIcon} alt="close-icon" />
                </div>
              </div>

              <div className="scroll-holder">
                <div className="scrollable-container">
                  <div className="vb-sidebar_wrapper">
                    <div className="vb-sidebar">
                      <div className="select-games">
                        <div className="select-games_txt ellipsis">{t(T.SELECT_GAMES)}</div>
                        <div
                          className={`select-games_arrow`}
                          onClick={() => setSidebarOpen(prev => !prev)}
                        >
                          <img src={SideBarOpenerIcon} alt="opener" />
                        </div>
                      </div>


                      <div className="vb-sidebar_item-list">
                        {groupedCategories?.[selectedCategory]?.map(game => (
                          <div
                            key={game.gameId}
                            className={`vb-sidebar_item ${selectedGame?.gameId === game.gameId ? 'active' : ''}`}
                            onClick={() => onGameSelect(game)}
                          >
                            <div className='vb-sidebar_info'>
                              <img src={GameItemIcon} alt={game?.gameName} className='icon-vb-sidebar' />

                              <span>{game?.gameName}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="iframe-wr">
            <GameFrame refCallback={refCallback} frameSource={gameFrameSource} frameNonce={frameNonce} />
          </div>
        </main>
      </div>
    ) : null
  );
});
